import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, computed, effect, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { CollectionView } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'
import { Subscription } from 'rxjs'

import { BaseComponent, DayOfWeekEx, FeatureType, Grid, ListItem, Meeting, MeetingPage, ModalSize, VpUser } from '../../shared'
import { MeetingEntityTypes } from '../../shared/services/meeting-hub.service'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { ActiveMeetingComponent } from '../active-meeting/active-meeting.component'
import { GoalsComponent } from '../goals/goals.component'
import { HeadlinesComponent } from '../headlines/headlines.component'
import { MeetingPagesComponent } from '../meeting-pages/meeting-pages.component'
import { RocksComponent } from '../rocks/rocks.component'
import { ToDosComponent } from '../to-dos/to-dos.component'
import { MeetingComponent } from './meeting.component'

@Component({
    templateUrl: './meetings.component.html',
    standalone: true,
    imports: [
        ActiveMeetingComponent,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        RocksComponent,
        GoalsComponent,
        ToDosComponent,
        HeadlinesComponent,
        MeetingPagesComponent,
    ],
})
export class MeetingsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch

    active = 1

    grid: Grid<Meeting>
    allUsers: VpUser[] = []
    gridSelection = signal<Meeting | null>(null)
    dayOfWeekMap = DayOfWeekEx.toDataMap()

    activeMeeting = signal<Meeting | null>(null)
    activeMeetingPages = signal<MeetingPage[]>([])

    /** Lower-cased ids of meetings that currently have a live (in-progress) session. */
    liveMeetingIds = signal<string[]>([])
    private livePollInterval: any = null
    private contentSyncSub?: Subscription
    private liveWatchSub?: Subscription

    userMeetings = signal<boolean>(true)
    boolList: ListItem<boolean>[] = [new ListItem(true, 'My Meetings'), new ListItem(false, 'All Meetings')]

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingEdit, FeatureType.MeetingsMeetingAdmin])
    }
    get canViewAdmin() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingAdmin])
    }

    canViewSelectedItem = computed(() => {
        if (this.canViewAdmin) return true
        if (!this.gridSelection()) return true
        const meeting = this.gridSelection()
        if (meeting && meeting.userIds.indexOf(this.services.authentication.userId()) > -1) return true
        return false
    })

    constructor() {
        super()
        this.grid = new Grid<Meeting>(this.services)
            .setupModalEditing(this.services.data.meetings, 'Meeting', MeetingComponent, () => new Meeting(), this, ModalSize.medium)
            .filterBy(item => [item.name])
            .doInitialSelection(false)
            .editOnDoubleClick(this.canEdit)

        this.queryPrereqData()
        this.refresh()
        this.startLivePolling()
        this.startLiveMeetingWatch()
        this.subscribeToContentSync()

        this.grid.selectionChanged.on(item => this.gridSelection.set(item))

        effect(() => {
            const view = this.grid.view as CollectionView
            if (view._filters.length > 1) view._filters.splice(1, 100) // keeps only the first item

            if (this.userMeetings()) {
                view._filters.push(x => x.userIds.indexOf(this.services.authentication.userId()) > -1)
            }
            this.grid.selectedItem = this.grid.view.items[0]
            this.grid.refresh()
        })
    }

    ngOnDestroy() {
        this.grid.selectionChanged.off(item => this.gridSelection.set(item))
        this.contentSyncSub?.unsubscribe()
        this.liveWatchSub?.unsubscribe()
        this.services.meetingHub.unwatchLiveMeetings().catch(() => {
            /* non-critical */
        })
        if (this.livePollInterval) {
            clearInterval(this.livePollInterval)
            this.livePollInterval = null
        }
    }

    private startLiveMeetingWatch(): void {
        this.services.meetingHub.watchLiveMeetings().catch(err => {
            console.error('Failed to watch live meetings', err)
        })

        this.liveWatchSub = this.services.meetingHub.liveMeetingChanged$.subscribe(update => {
            if (this.activeMeeting()) return
            this.applyLiveMeetingChange(update.meetingId, update.isLive)
        })
    }

    private applyLiveMeetingChange(meetingId: string, isLive: boolean): void {
        const id = meetingId.toLowerCase()
        this.liveMeetingIds.update(ids => {
            const has = ids.includes(id)
            if (isLive && !has) return [...ids, id]
            if (!isLive && has) return ids.filter(x => x !== id)
            return ids
        })
        this.grid.refresh()
    }

    private subscribeToContentSync(): void {
        this.contentSyncSub = this.services.meetingHub.contentChanged$.subscribe(notification => {
            const active = this.activeMeeting()
            if (!active || notification.meetingId.toLowerCase() !== active.id.toLowerCase()) return
            if (notification.entityType !== MeetingEntityTypes.MeetingPage) return
            this.refreshActiveMeetingPages(false)
        })

        this.contentSyncSub.add(
            this.services.meetingHub.contentResync$.subscribe(meetingId => {
                const active = this.activeMeeting()
                if (!active || meetingId.toLowerCase() !== active.id.toLowerCase()) return
                this.refreshActiveMeetingPages(false)
            }),
        )
    }

    private refreshActiveMeetingPages(showSpinner: boolean): void {
        const active = this.activeMeeting()
        if (!active) return

        const params = new HttpParams().set('meetingId', active.id)
        this.services.data
            .load(showSpinner, showSpinner, this.services.data.meetingPage.queryAllByAction('GetByMeetingId', params))
            .subscribe({
                next: result => this.activeMeetingPages.set(result[0]),
            })
    }

    private startLivePolling() {
        this.loadLiveMeetings()
        this.livePollInterval = setInterval(() => {
            // Only poll while browsing the list (not while inside an active meeting).
            if (!this.activeMeeting()) this.loadLiveMeetings()
        }, 15000)
    }

    private loadLiveMeetings() {
        this.services.data.get<string[]>('api/Meeting/GetLiveMeetingIds').subscribe({
            next: ids => this.liveMeetingIds.set((ids || []).map(id => id.toLowerCase())),
            error: () => {
                /* non-critical; leave badges as-is */
            },
        })
    }

    isLive(meeting: Meeting | null | undefined): boolean {
        if (!meeting) return false
        return this.liveMeetingIds().indexOf(meeting.id.toLowerCase()) > -1
    }

    queryPrereqData() {
        this.services.data
            .load(
                true,
                true,
                this.services.data.users.queryAllByAction('GetForLookup'),
                this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
            )
            .subscribe({
                next: result => {
                    this.allUsers = result[1]
                    this.refresh()
                },
            })
    }

    refresh() {
        this.cancelableRequest(
            'MeetingsRefresh',
            true,
            this.services.data.load(true, true, this.services.data.meetings.queryAll()).subscribe({
                next: () => {
                    this.grid.items = this.services.data.meetings.items

                    const activeMeetingId = localStorage.getItem('activeMeetingId')
                    if (activeMeetingId) {
                        const activeMeeting = this.services.data.meetings.items.find(x => x.id === activeMeetingId)
                        if (activeMeeting) {
                            this.meet(activeMeeting)
                        }
                    }
                    setTimeout(() => {
                        this.initialLoadingComplete()
                    }, 500)
                },
            }),
        )
    }

    getUsers(ids: string[]) {
        return this.services.data.users.items.filter(x => ids.indexOf(x.id) > -1)
    }

    meet(meeting: Meeting) {
        if (!this.canViewSelectedItem()) {
            this.services.notification.info('You do not have permission to start this meeting.')
            return
        }

        const params = new HttpParams().set('meetingId', meeting.id)
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.meetingPage.queryAllByAction('GetByMeetingId', params)).subscribe({
                next: result => {
                    this.activeMeetingPages.set(result[0])
                    this.activeMeeting.set(meeting)
                    this.services.meetingHub.activeMeetingId.set(meeting.id)
                    localStorage.setItem('activeMeetingId', meeting.id)
                },
            }),
        )
    }

    handleEndMeeting() {
        const activeMeeting = this.activeMeeting()
        if (activeMeeting) {
            // Clear timer state for this meeting
            this.services.meetingTimer.clearTimerState(activeMeeting.id)
            localStorage.removeItem(`activeMeetingPageIndex_${activeMeeting.id}`)
        }

        this.activeMeeting.set(null)
        this.services.meetingHub.activeMeetingId.set(null)
        this.services.meetingHub.isLead.set(false)
        localStorage.removeItem('activeMeetingId')
        // Clear legacy global key used before per-meeting scoping.
        localStorage.removeItem('activeMeetingPageIndex')
        this.loadLiveMeetings()
        this.services.meetingHub.watchLiveMeetings().catch(() => {
            /* non-critical */
        })
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }
}
